/* eslint-disable no-shadow */

import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { connect } from 'react-redux';
import { defer } from 'lodash';

import TranslationProvider, { getTranslation } from '../../providers/TranslationProvider';
import Button from '../Button';
import Icon from '../Icon';
import Link from '../Link';
import Rating from '../Rating';
import {
  ariaLabelVenue, priceRangeToString, parameterize,
} from '../../utils';

import {
  setSelectedCoreISODate,
} from '../../reducers/resyCoreSlice';

import LocationPin from '../../svg/icons/ic_location_20x20.svg';
import { selectIsAuthenticated } from '../../reducers/authTokenSlice';
import './PlatinumVenueCard.scss';
import { setLocationPath } from '../../utils/locationUtils';

const PlatinumVenueCard = (props) => {
  const {
    $rootScope,
    event,
    cuisine,
    currency,
    image,
    isInventoryComingSoon,
    locationSlug,
    name,
    neighborhood,
    priceRange,
    rating,
    searchSessionId,
    selectedDate,
    selectedSeats,
    setSelectedCoreISODate,
    totalRatings,
    urlSlug,
  } = props;
  const pathname = `cities/${locationSlug}/venues/${urlSlug}`;

  const query = {
    date: selectedDate,
    seats: selectedSeats,
  };

  const price = priceRange ? priceRangeToString(priceRange, currency) : null;

  const handleBookPlatinumNightLink = () => {
    setSelectedCoreISODate(selectedDate);
    event('click booking slot', {
      module: 'platinum night',
      searchSessionId,
    });

    setLocationPath(`/${pathname}?${parameterize(query)}`, defer(() => {
      $rootScope.$apply();
    }));
  };

  const renderLinkAndHeading = () => {
    if (isInventoryComingSoon) {
      return (
        <h2
          className={cx('VenueCard__content__info__name skeleton', {
            'no-skeleton': name,
          })}
        >
          {name}
        </h2>
      );
    }

    return (
      <Link
        onClick={handleBookPlatinumNightLink}
        aria-label={ariaLabelVenue(name, cuisine, neighborhood, price)}
        variant="article"
      >
        <h2
          className={cx('VenueCard__content__info__name skeleton', {
            'no-skeleton': name,
          })}
        >
          {name}
        </h2>
      </Link>
    );
  };

  const renderButtons = () => {
    if (isInventoryComingSoon) {
      return (
        <TranslationProvider translationKey="coming_soon">
          <Button
            md
            disabled
          >
            Coming Soon
          </Button>
        </TranslationProvider>
      );
    }

    if (selectedDate) {
      const formattedSelectDate = moment(selectedDate).format('MMMM DD');

      const bookButtonAriaLabel = getTranslation({
        translationKey: 'platinum_book_button_aria',
        translationValues: { date: formattedSelectDate },
        fallback: `Exclusive Night, Book ${formattedSelectDate}`,
      });
      return (
        <TranslationProvider translationKey="button_book_date" translationValues={{ date: formattedSelectDate }}>
          <Button
            role="link"
            md
            onClick={handleBookPlatinumNightLink}
            aria-label={bookButtonAriaLabel}
          >
            {`Book ${formattedSelectDate}`}
          </Button>
        </TranslationProvider>
      );
    }

    return null;
  };

  const renderVenueInfo = () => {
    if (name) {
      return (
        <div className="PlatinumVenueCard__attributes">
          <div className="PlatinumVenueCard__content__info__rating">
            <Rating
              scale={5}
              score={rating}
              totalRatings={totalRatings}
            />
            &nbsp;•&nbsp;
          </div>
          <div className="PlatinumVenueCard__content__info__cuisine">
            {cuisine}
            &nbsp;•&nbsp;
          </div>
          <div className="PlatinumVenueCard__content__info__price">
            {price}
          </div>
        </div>
      );
    }

    return null;
  };

  const renderNeighborhood = () => {
    if (neighborhood) {
      return (
        <div
          className={cx('VenueCard__content__info__neighborhood skeleton', {
            'no-skeleton': name,
          })}
        >
          <Icon component={LocationPin} name="location-pin" />
          {neighborhood}
        </div>
      );
    }

    return null;
  };

  const getDayAndMonth = (date) => {
    const [month, day] = date.split(' ');

    return {
      month,
      day,
    };
  };

  const selectedDateFormatted = getDayAndMonth(moment(selectedDate).format('MMM DD'));
  return (
    <div
      className={cx('PlatinumVenueCard', {
        faded: isInventoryComingSoon,
      })}
      aria-disabled={isInventoryComingSoon}
    >
      <div
        alt=""
        className="PlatinumVenueCard__image"
        style={{
          backgroundImage: `url(${image})`,
        }}
      >
        {selectedDate && (
          <span className="PlatinumVenueCard__image__date">
            <span className="PlatinumVenueCard__image__date__month">{selectedDateFormatted.month}</span>
            <span className="PlatinumVenueCard__image__date__day">{selectedDateFormatted.day}</span>
          </span>
        )}
        <div
          className={cx('skeleton', {
            'no-skeleton': (name && image) || image === null,
          })}
        />
      </div>

      <div className="PlatinumVenueCard__content">
        <div className="PlatinumVenueCard__content__info">
          {renderLinkAndHeading()}
          {renderVenueInfo()}
          {renderNeighborhood()}
        </div>

        {renderButtons()}
      </div>
    </div>
  );
};

PlatinumVenueCard.propTypes = {
  $rootScope: PropTypes.shape({
    $apply: PropTypes.func.isRequired,
  }).isRequired,
  event: PropTypes.func.isRequired,
  cuisine: PropTypes.string,
  currency: PropTypes.string,
  image: PropTypes.string,
  isInventoryComingSoon: PropTypes.bool,
  locationSlug: PropTypes.string,
  name: PropTypes.string,
  neighborhood: PropTypes.string,
  priceRange: PropTypes.number,
  rating: PropTypes.number,
  searchSessionId: PropTypes.string,
  selectedDate: PropTypes.string,
  selectedSeats: PropTypes.number.isRequired,
  setSelectedCoreISODate: PropTypes.func.isRequired,
  totalRatings: PropTypes.number,
  urlSlug: PropTypes.string.isRequired,
};

PlatinumVenueCard.defaultProps = {
  cuisine: null,
  currency: '$',
  image: undefined,
  isInventoryComingSoon: false,
  locationSlug: 'undefined',
  name: null,
  neighborhood: null,
  priceRange: null,
  rating: 5,
  searchSessionId: '',
  selectedDate: null,
  totalRatings: null,
};

const mapStateToProps = (state) => ({
  isAuthenticated: selectIsAuthenticated(state),
  searchSessionId: state.venueSearch?.metadata?.searchSessionId,
});

const mapDispatchToProps = ({
  setSelectedCoreISODate,
});

export { PlatinumVenueCard as ComponentUnderTest, mapStateToProps };
export default connect(mapStateToProps, mapDispatchToProps)(PlatinumVenueCard);
